import { Clear } from "@mui/icons-material";
import type { TextFieldProps } from "@mui/material";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import React from "react";

import { AutocompleteWithWrap } from "../AutocompleteWithWrap/AutocompleteWithWrap";

export interface ClearableFilterAutocompleteProps<T> {
  /** Field label, shown shrunk above the input. */
  label: string;
  options: T[];
  /** The currently selected option. */
  value: NonNullable<T> | undefined;
  /** Whether a non-default value is active — controls the clear button. */
  active: boolean;
  onChange: (option: T | null) => void;
  onClear: () => void;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue: (option: T, selected: T) => boolean;
}

// A single-select filter dropdown with a label and an inline "clear" button.
// Shared by the permissions filters (Account, Source) so the autocomplete
// layout and clear-adornment pattern live in one place.
function ClearableFilterAutocomplete<T>({
  label,
  options,
  value,
  active,
  onChange,
  onClear,
  getOptionLabel,
  isOptionEqualToValue,
}: ClearableFilterAutocompleteProps<T>) {
  return (
    <AutocompleteWithWrap<T, false, true, false>
      disableClearable
      value={value}
      options={options}
      onChange={(_event, option) => onChange(option)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      autoComplete
      autoHighlight
      // We render our own clear button as the endAdornment instead of the
      // popup arrow; tell Autocomplete there's no popup icon so it stops
      // reserving padding-right for it (which otherwise floats our X left).
      forcePopupIcon={false}
      sx={{ width: (theme) => theme.spacing(35) }}
      renderInput={(props: TextFieldProps) => (
        <TextField
          {...props}
          label={label}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...props.InputProps,
            endAdornment: active ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={onClear}
                  edge="end"
                  title="Clear filter"
                  sx={{ marginRight: "0px" }}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      )}
    />
  );
}

export { ClearableFilterAutocomplete };
